/**
 * The merchandise request journey (brief §5 slice A, J2), the one flow behind
 * `/estimates/new`. Three steps — Request, Delivery, Finalise — because the
 * price does not come from a Tharstern schema: the merchandise team quotes it
 * in Promo Office. Finalise creates the draft estimate and raises their
 * ticket; the reference is what they type onto the quote so it attaches
 * itself.
 *
 * The request is a project name, one deadline for the whole estimate and
 * optional notes (decided 2026-10-07), plus **request lines** — one per item
 * the customer wants quoted (decided 2026-10-01). Not to be confused with a
 * linked quote's lines, which arrive later from Promo Office with prices.
 *
 * Logos are a two-step, mirroring catalogue artwork: create posts the request
 * as JSON, then each line's files are uploaded against that line of the draft
 * that now exists. The consequence is user-visible and handled honestly — the
 * draft and its reference exist before the files land, so a failed upload is
 * shown per file with a retry and never discards the request.
 */
import {
  extractFieldErrors,
  applyFieldErrors,
} from "@shared-core/data/validation";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { MerchandiseDraftEstimate } from "@shared-core/data/merchandise-types";
import type {
  DeliveryMode,
  DeliverySelection,
} from "@shared-core/data/delivery-types";
import { DELIVERY_MODE_LABELS } from "@shared-core/data/delivery-types";
import {
  formatBytes,
  formatLongDateOrDash,
} from "@shared-core/domain/formatters";
import { describeRequestLineCount } from "@shared-core/domain/merchandise-presentation";
import { paths } from "@shared-core/routing/routes";
import {
  EntityCombobox,
  type EntityOption,
} from "../../app/components/patterns/EntityCombobox";
import {
  Stepper,
  type StepperStep,
} from "../../app/components/patterns/Stepper";
import { DeliverySection } from "../../app/components/domain/delivery/DeliverySection";
import { Button } from "../../app/components/primitives/button";
import { Card } from "../../app/components/primitives/card";
import { Input } from "../../app/components/primitives/input";
import { Label } from "../../app/components/primitives/label";
import { Textarea } from "../../app/components/primitives/textarea";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Gift,
  Loader2,
  Paperclip,
  Plus,
  Ticket,
  Trash2,
  X,
} from "lucide-react";

type MerchStep = "request" | "delivery" | "finalise";

const STEP_ORDER: MerchStep[] = ["request", "delivery", "finalise"];

/** Merchandise ships from Plymouth, where the merchandise team sits. */
const MERCHANDISE_PRODUCTION_SITE = "Plymouth - Sisna Park Road";

/** No "Hold" and no "Quote only" here: the merchandise team quotes carriage on
 *  a real destination. Delivery itself stays optional (pending the business). */
const MERCHANDISE_DELIVERY_MODES: readonly DeliveryMode[] = [
  "ship",
  "collect",
  "multi",
  "mailing",
];

/** One request line as the form holds it. `key` is stable across removals so
 *  React and the touched-field set never confuse two lines; the logo files
 *  stay real `File` objects until they are uploaded against the created line. */
type RequestLineDraft = {
  key: number;
  itemType: string;
  quantity: string;
  logoColours: string;
  budget: string;
  sourcingCityCode: string;
  files: File[];
};

type RequiredLineField = "itemType" | "quantity" | "logoColours";

const REQUIRED_LINE_FIELDS: { field: RequiredLineField; missing: string }[] = [
  { field: "itemType", missing: "item type" },
  { field: "quantity", missing: "quantity" },
  { field: "logoColours", missing: "logo colours" },
];

/** The same sentences the backend returns, so a field reads identically
 *  whether the browser or the server caught it. */
const REQUIRED_LINE_MESSAGES: Record<RequiredLineField, string> = {
  itemType: "Say what the item is",
  quantity: "Enter a quantity, or the price breaks to quote",
  logoColours: 'Give the logo colours, or "None"',
};

/** The same sentence the backend returns for a missing estimate deadline. */
const DEADLINE_REQUIRED_MESSAGE = "Pick a deadline for the estimate";

function emptyLine(key: number): RequestLineDraft {
  return {
    key,
    itemType: "",
    quantity: "",
    logoColours: "",
    budget: "",
    sourcingCityCode: "",
    files: [],
  };
}

type FlowState =
  | { kind: "form" }
  | { kind: "submitting" }
  | { kind: "created"; request: MerchandiseDraftEstimate }
  | { kind: "error"; message: string };

/** One logo's journey to its created request line. `failed` is a state the
 *  CSR can act on, not a dead end — the draft stands either way. */
type FileUpload = {
  file: File;
  lineNo: number;
  requestLineId: string;
  status: "pending" | "uploading" | "done" | "failed";
};

export function customerLabel(
  id: string,
  name: string | null | undefined,
): string {
  const trimmed = (name ?? "").trim();
  return trimmed ? `${id} — ${trimmed}` : id;
}

/** The server's field path for one line's field — `.0` indices, per
 *  `.claude/rules/api-validation.md`. */
const linePath = (index: number, field: string) =>
  `request.lines.${index}.${field}`;

export function MerchandiseRequestFlow({
  initialCustomer,
  onBackToEstimates,
}: {
  /** The customer already known from the URL, if any. */
  initialCustomer: EntityOption | null;
  onBackToEstimates: () => void;
}) {
  const adapter = useDataAdapter();
  /** The server's 400, keyed by field path. Cleared on re-submit, on editing
   *  the field it names, and wholesale when a line is removed (the indices it
   *  was keyed on no longer point at the same lines). */
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  /** Fields the CSR has left, keyed `<line key>.<field>` (or the field path
   *  for the estimate's own fields), so a blank required field is marked once
   *  they have moved on — not before. */
  const [touched, setTouched] = useState<Set<string>>(() => new Set());
  const [step, setStep] = useState<MerchStep>("request");
  const [customer, setCustomer] = useState<EntityOption | null>(
    initialCustomer,
  );
  const [projectName, setProjectName] = useState("");
  /** `YYYY-MM-DD`, the date input's own value; empty until picked. */
  const [deadline, setDeadline] = useState("");
  const [notes, setNotes] = useState("");
  const [deliveryNotes, setDeliveryNotes] = useState("");
  const nextLineKey = useRef(1);
  const [lines, setLines] = useState<RequestLineDraft[]>(() => [emptyLine(0)]);
  const [uploads, setUploads] = useState<FileUpload[]>([]);
  const [deliveryVisited, setDeliveryVisited] = useState(false);
  const [deliverySelection, setDeliverySelection] =
    useState<DeliverySelection | null>(null);
  const [flowState, setFlowState] = useState<FlowState>({ kind: "form" });

  // A customer prefilled from the URL resolves after the page mounts; adopt it
  // if the rep has not picked one here yet.
  useEffect(() => {
    if (initialCustomer) setCustomer((current) => current ?? initialCustomer);
  }, [initialCustomer]);

  const clearFieldError = (path: string) =>
    setFieldErrors((prev) => {
      if (!(path in prev)) return prev;
      const next = { ...prev };
      delete next[path];
      return next;
    });

  const updateLine = <K extends keyof RequestLineDraft>(
    index: number,
    key: K,
    value: RequestLineDraft[K],
  ) => {
    setLines((prev) =>
      prev.map((line, i) => (i === index ? { ...line, [key]: value } : line)),
    );
    clearFieldError(linePath(index, key));
  };
  const touch = (line: RequestLineDraft, field: string) =>
    setTouched((prev) =>
      prev.has(`${line.key}.${field}`)
        ? prev
        : new Set(prev).add(`${line.key}.${field}`),
    );
  const addLine = () => {
    const key = nextLineKey.current;
    nextLineKey.current += 1;
    setLines((prev) => [...prev, emptyLine(key)]);
    clearFieldError("request.lines");
  };
  const removeLine = (index: number) => {
    setLines((prev) => prev.filter((_, i) => i !== index));
    setFieldErrors((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(
          ([field]) => !field.startsWith("request.lines"),
        ),
      ),
    );
  };

  const missingLineFields = (line: RequestLineDraft) =>
    REQUIRED_LINE_FIELDS.filter(({ field }) => !line[field].trim());

  // Request is the only step that gates: without a customer, a project name,
  // a deadline and at least one complete request line the merchandise team
  // cannot quote.
  // Delivery is optional — the notes and address help, but the ticket can be
  // raised without them.
  const requestMissing: string[] = [];
  if (!customer) requestMissing.push("pick a customer");
  if (!projectName.trim()) requestMissing.push("name the project");
  if (!deadline) requestMissing.push("pick a deadline");
  if (lines.length === 0) requestMissing.push("add a request line");
  lines.forEach((line, index) => {
    const missing = missingLineFields(line);
    if (missing.length) {
      requestMissing.push(
        `line ${index + 1}: ${missing.map((m) => m.missing).join(", ")}`,
      );
    }
  });
  const requestComplete = requestMissing.length === 0;

  const steps: StepperStep[] = useMemo(
    () => [
      { id: "request", label: "Request", complete: requestComplete },
      { id: "delivery", label: "Delivery", complete: deliveryVisited },
      {
        id: "finalise",
        label: "Finalise",
        complete: flowState.kind === "created",
      },
    ],
    [requestComplete, deliveryVisited, flowState.kind],
  );

  const goTo = (next: MerchStep) => {
    if (next === "delivery") setDeliveryVisited(true);
    setStep(next);
  };
  const goNext = () => {
    const index = STEP_ORDER.indexOf(step);
    if (index < STEP_ORDER.length - 1) goTo(STEP_ORDER[index + 1]);
  };

  const uploadFile = async (
    draftEstimateId: string,
    upload: FileUpload,
    index: number,
  ) => {
    setUploads((prev) =>
      prev.map((u, i) => (i === index ? { ...u, status: "uploading" } : u)),
    );
    try {
      const updated = await adapter.attachDraftEstimateFile(
        draftEstimateId,
        upload.requestLineId,
        upload.file,
      );
      // Keep the draft the upload returned, so the created screen's file list
      // is the server's and not the form's hopeful copy of it.
      setFlowState((prev) =>
        prev.kind === "created" ? { kind: "created", request: updated } : prev,
      );
      setUploads((prev) =>
        prev.map((u, i) => (i === index ? { ...u, status: "done" } : u)),
      );
    } catch {
      setUploads((prev) =>
        prev.map((u, i) => (i === index ? { ...u, status: "failed" } : u)),
      );
    }
  };

  const submit = async () => {
    if (!customer) return;
    setFieldErrors({});
    setFlowState({ kind: "submitting" });
    try {
      const request = await adapter.createMerchandiseDraftEstimate({
        customerCode: customer.id,
        request: {
          projectName: projectName.trim(),
          deadline: deadline || null,
          notes: notes.trim(),
          lines: lines.map((line) => ({
            itemType: line.itemType.trim(),
            quantity: line.quantity.trim(),
            logoColours: line.logoColours.trim(),
            budget: line.budget.trim(),
            sourcingCityCode: line.sourcingCityCode.trim(),
          })),
          // The mode travels as its own field. Concatenating a label into the
          // free-text notes lost it: no backend can parse it back out, and the
          // ticket wants the mode on its own line.
          deliveryMode: deliverySelection?.mode ?? null,
          deliveryNotes: deliveryNotes.trim(),
        },
      });
      // The success screen is reached the moment the draft exists — its
      // reference is already on the merchandise team's ticket. Logos follow,
      // each against the created line in the same position (the server keeps
      // the order the lines were sent in, as `lineNo`).
      const created = [...request.request.lines].sort(
        (a, b) => a.lineNo - b.lineNo,
      );
      const queued: FileUpload[] = lines.flatMap((line, index) => {
        const target = created[index];
        return target
          ? line.files.map((file) => ({
              file,
              lineNo: target.lineNo,
              requestLineId: target.id,
              status: "pending" as const,
            }))
          : [];
      });
      setFlowState({ kind: "created", request });
      setUploads(queued);
      for (let index = 0; index < queued.length; index += 1) {
        await uploadFile(request.id, queued[index], index);
      }
    } catch (e) {
      const errors = extractFieldErrors(e);
      const mapped: Record<string, string> = {};
      applyFieldErrors((field, error) => {
        mapped[field] = error.message;
      }, errors);
      setFieldErrors(mapped);
      if (errors.length) {
        setStep(
          errors.some((error) => !error.field.startsWith("request.delivery"))
            ? "request"
            : "delivery",
        );
      }
      setFlowState({
        kind: "error",
        message: errors.length
          ? `${errors.length} ${errors.length === 1 ? "field needs" : "fields need"} attention before this merchandise request can be created.`
          : "The merchandise request could not be created. Check your requests before trying again.",
      });
    }
  };

  const addFiles = (index: number, picked: FileList | null) => {
    if (!picked) return;
    // Copied now, not inside the updater: the input is cleared straight after
    // this call so the same file can be re-picked, and a live FileList read
    // later by React would be empty by then.
    const added = Array.from(picked);
    setLines((prev) =>
      prev.map((line, i) =>
        i === index
          ? {
              ...line,
              files: [
                ...line.files,
                ...added.filter(
                  (candidate) =>
                    !line.files.some(
                      (existing) => existing.name === candidate.name,
                    ),
                ),
              ],
            }
          : line,
      ),
    );
  };

  if (flowState.kind === "created") {
    const draftEstimateId = flowState.request.id;
    return (
      <CreatedScreen
        request={flowState.request}
        uploads={uploads}
        onRetryUpload={(index) =>
          void uploadFile(draftEstimateId, uploads[index], index)
        }
        onBackToEstimates={onBackToEstimates}
      />
    );
  }

  const submitting = flowState.kind === "submitting";
  /** The message to show for a field: the server's, or the browser's for a
   *  required field the CSR has left blank after touching it. */
  const errorFor = (path: string, clientMessage?: string | null) =>
    fieldErrors[path] ?? clientMessage ?? null;
  const errorProps = (path: string, message: string | null) => ({
    "aria-invalid": message ? true : undefined,
    "aria-describedby": message ? `merch-error-${path}` : undefined,
  });
  const fieldError = (path: string, message: string | null) =>
    message ? (
      <p id={`merch-error-${path}`} className="text-sm text-destructive">
        {message}
      </p>
    ) : null;
  const simpleError = (path: string) => {
    const message = errorFor(path);
    return {
      props: errorProps(path, message),
      node: fieldError(path, message),
    };
  };

  const customerError = simpleError("customerCode");
  const projectError = simpleError("request.projectName");
  const deadlineMessage = errorFor(
    "request.deadline",
    touched.has("request.deadline") && !deadline
      ? DEADLINE_REQUIRED_MESSAGE
      : null,
  );
  const deadlineError = {
    props: errorProps("request.deadline", deadlineMessage),
    node: fieldError("request.deadline", deadlineMessage),
  };
  const notesError = simpleError("request.notes");
  const linesError = simpleError("request.lines");
  const deliveryModeError = simpleError("request.deliveryMode");
  const deliveryNotesError = simpleError("request.deliveryNotes");

  return (
    <div className="space-y-4 max-w-6xl mx-auto">
      <h1 className="sr-only">New merchandise request</h1>
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onBackToEstimates}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to estimates
        </button>
        <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Gift className="size-4" aria-hidden />
          Promotional Merchandise
        </span>
      </div>

      <div className="sticky top-0 z-20 -mx-2 px-2 py-2 bg-[color:var(--color-background)] border-b border-border">
        <Stepper
          steps={steps}
          value={step}
          onChange={(id) => goTo(id as MerchStep)}
        />
      </div>

      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8 lg:items-start">
        <div className="space-y-4 min-w-0">
          {flowState.kind === "error" && (
            <Card
              role="alert"
              className="p-4 border-[color:var(--color-destructive)]"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="size-5 text-[color:var(--color-destructive)] shrink-0 mt-0.5" />
                <p className="font-medium text-[color:var(--color-destructive)]">
                  {flowState.message}
                </p>
              </div>
            </Card>
          )}

          {step === "request" && (
            <>
              <div className="space-y-1">
                <h2 className="text-lg font-semibold">
                  What does the customer want?
                </h2>
                <p className="text-sm text-muted-foreground max-w-prose">
                  Merchandise is quoted by the merchandise team in Promo Office,
                  not in Tharstern. Add a line for each item the customer wants
                  quoted; the request goes to them as a ticket with everything
                  below.
                </p>
              </div>

              <Card className="p-4 space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Customer
                </h3>
                <EntityCombobox
                  value={customer}
                  onChange={setCustomer}
                  // Customers linked to Tharstern only: a draft estimate
                  // records a Tharstern code, so a customer created in CIS
                  // waits for draft estimates to key on the CIS id (v1
                  // completion C5, ruling 5).
                  fetchOptions={async (q) => {
                    const page = await adapter.listCisCustomers({
                      q,
                      pageSize: 10,
                      isActive: true,
                      isLinked: true,
                    });
                    return page.items.flatMap((c) =>
                      c.tharstern_customer_code
                        ? [
                            {
                              id: c.tharstern_customer_code,
                              label: customerLabel(
                                c.tharstern_customer_code,
                                c.name,
                              ),
                              hint: c.name,
                            },
                          ]
                        : [],
                    );
                  }}
                  placeholder="Search by code or name…"
                  ariaLabel="Customer"
                  {...customerError.props}
                  disabled={submitting}
                />
                {customerError.node}
              </Card>

              <Card className="p-4 space-y-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  The request
                </h3>
                <div className="space-y-1">
                  <Label htmlFor="merch-project-name">Project name</Label>
                  <Input
                    id="merch-project-name"
                    {...projectError.props}
                    value={projectName}
                    onChange={(e) => {
                      setProjectName(e.target.value);
                      clearFieldError("request.projectName");
                    }}
                    placeholder="e.g. Spring open day"
                    disabled={submitting}
                  />
                  <p className="text-xs text-muted-foreground">
                    What the job is called here and in Promo Office. It cannot
                    be changed once the request is sent to the merchandise team.
                  </p>
                  {projectError.node}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="merch-deadline">Deadline</Label>
                  <Input
                    id="merch-deadline"
                    type="date"
                    className="sm:max-w-56"
                    {...deadlineError.props}
                    value={deadline}
                    onChange={(e) => {
                      setDeadline(e.target.value);
                      clearFieldError("request.deadline");
                    }}
                    onBlur={() =>
                      setTouched((prev) =>
                        prev.has("request.deadline")
                          ? prev
                          : new Set(prev).add("request.deadline"),
                      )
                    }
                    disabled={submitting}
                  />
                  <p className="text-xs text-muted-foreground">
                    When the customer needs the goods by — one date for the
                    whole estimate.
                  </p>
                  {deadlineError.node}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="merch-notes">Notes</Label>
                  <Textarea
                    id="merch-notes"
                    {...notesError.props}
                    rows={3}
                    value={notes}
                    onChange={(e) => {
                      setNotes(e.target.value);
                      clearFieldError("request.notes");
                    }}
                    placeholder="Anything the merchandise team should know about this estimate"
                    disabled={submitting}
                  />
                  {notesError.node}
                </div>
              </Card>

              <section
                aria-labelledby="merch-lines-heading"
                className="space-y-3"
              >
                <div className="flex items-center justify-between gap-3">
                  <h3
                    id="merch-lines-heading"
                    className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >
                    Request lines
                  </h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addLine}
                    disabled={submitting}
                  >
                    <Plus className="size-4" aria-hidden />
                    Add line
                  </Button>
                </div>
                {linesError.node}
                {lines.map((line, index) => (
                  <RequestLineEditor
                    key={line.key}
                    line={line}
                    index={index}
                    canRemove={lines.length > 1}
                    disabled={submitting}
                    errorFor={(field) => {
                      const required = REQUIRED_LINE_FIELDS.find(
                        (r) => r.field === field,
                      );
                      const isBlankAndTouched =
                        required !== undefined &&
                        touched.has(`${line.key}.${field}`) &&
                        !line[required.field].trim();
                      return errorFor(
                        linePath(index, field),
                        isBlankAndTouched
                          ? REQUIRED_LINE_MESSAGES[required.field]
                          : null,
                      );
                    }}
                    errorProps={errorProps}
                    fieldError={fieldError}
                    onChange={(key, value) => updateLine(index, key, value)}
                    onBlur={(field) => touch(line, field)}
                    onAddFiles={(picked) => addFiles(index, picked)}
                    onRemoveFile={(name) =>
                      updateLine(
                        index,
                        "files",
                        line.files.filter((f) => f.name !== name),
                      )
                    }
                    onRemove={() => removeLine(index)}
                  />
                ))}
              </section>
            </>
          )}

          {step === "delivery" && (
            <>
              <div className="space-y-1">
                <h2 className="text-lg font-semibold">Delivery</h2>
                <p className="text-sm text-muted-foreground max-w-prose">
                  Where the goods go once ordered. Optional now — the
                  merchandise team quotes carriage on the address, so knowing it
                  early saves a round-trip.
                </p>
              </div>
              {customer ? (
                <fieldset {...deliveryModeError.props}>
                  <DeliverySection
                    key={customer.id}
                    customerCode={customer.id}
                    productionSite={MERCHANDISE_PRODUCTION_SITE}
                    allowedModes={MERCHANDISE_DELIVERY_MODES}
                    onChange={setDeliverySelection}
                    disabled={submitting}
                  />
                  {deliveryModeError.node}
                </fieldset>
              ) : (
                <Card className="p-6 text-sm text-muted-foreground">
                  Pick a customer on the Request step to choose from their
                  addresses.
                </Card>
              )}
              <Card className="p-4 space-y-2">
                <Label htmlFor="merch-delivery-notes">Delivery notes</Label>
                <Textarea
                  id="merch-delivery-notes"
                  {...deliveryNotesError.props}
                  rows={3}
                  value={deliveryNotes}
                  onChange={(e) => {
                    setDeliveryNotes(e.target.value);
                    clearFieldError("request.deliveryNotes");
                  }}
                  placeholder="e.g. Deliver to reception, weekdays before 3pm"
                  disabled={submitting}
                />
                {deliveryNotesError.node}
              </Card>
            </>
          )}

          {step === "finalise" && (
            <>
              <div className="space-y-1">
                <h2 className="text-lg font-semibold">Finalise</h2>
                <p className="text-sm text-muted-foreground max-w-prose">
                  Creating the merchandise request gives it a reference and
                  raises a ticket to the merchandise team. They quote in Promo
                  Office and type the reference onto the quote; the price
                  appears on the request once they have.
                </p>
              </div>
              <RequestSummary
                customer={customer}
                projectName={projectName}
                deadline={deadline}
                notes={notes}
                lines={lines}
                deliverySelection={deliverySelection}
                deliveryNotes={deliveryNotes}
              />
              {!requestComplete && (
                <Card className="p-4 border-[color:var(--color-warning)] text-sm">
                  <span className="font-medium">
                    Before this can be created:
                  </span>{" "}
                  {requestMissing.join("; ")}.{" "}
                  <button
                    type="button"
                    className="underline"
                    onClick={() => goTo("request")}
                  >
                    Back to Request
                  </button>
                </Card>
              )}
            </>
          )}

          <div className="flex items-center justify-between gap-3 pt-2">
            <div className="text-sm text-muted-foreground">
              {step === "request" && !requestComplete && (
                <>To continue: {requestMissing.join("; ")}.</>
              )}
            </div>
            {step === "finalise" ? (
              <Button
                onClick={submit}
                disabled={!requestComplete || submitting}
              >
                {submitting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Ticket className="size-4" />
                )}
                Create merchandise request
              </Button>
            ) : (
              <Button
                variant={
                  step === "request" && !requestComplete ? "outline" : "default"
                }
                onClick={goNext}
                disabled={step === "request" && !requestComplete}
              >
                Next: {step === "request" ? "Delivery" : "Finalise"}
                <ArrowRight className="size-4" />
              </Button>
            )}
          </div>
        </div>

        <aside className="hidden lg:block lg:sticky lg:top-16">
          <RequestSummary
            customer={customer}
            projectName={projectName}
            deadline={deadline}
            notes={notes}
            lines={lines}
            deliverySelection={deliverySelection}
            deliveryNotes={deliveryNotes}
            compact
          />
        </aside>
      </div>
    </div>
  );
}

function RequestLineEditor({
  line,
  index,
  canRemove,
  disabled,
  errorFor,
  errorProps,
  fieldError,
  onChange,
  onBlur,
  onAddFiles,
  onRemoveFile,
  onRemove,
}: {
  line: RequestLineDraft;
  index: number;
  canRemove: boolean;
  disabled: boolean;
  errorFor: (field: string) => string | null;
  errorProps: (
    path: string,
    message: string | null,
  ) => { "aria-invalid"?: boolean; "aria-describedby"?: string };
  fieldError: (path: string, message: string | null) => React.ReactNode;
  onChange: <K extends keyof RequestLineDraft>(
    key: K,
    value: RequestLineDraft[K],
  ) => void;
  onBlur: (field: string) => void;
  onAddFiles: (picked: FileList | null) => void;
  onRemoveFile: (name: string) => void;
  onRemove: () => void;
}) {
  const id = (field: string) => `merch-line-${line.key}-${field}`;
  const bind = (field: Exclude<keyof RequestLineDraft, "key" | "files">) => {
    const path = linePath(index, field);
    const message = errorFor(field);
    return {
      input: {
        id: id(field),
        value: line[field],
        onChange: (
          e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => onChange(field, e.target.value),
        onBlur: () => onBlur(field),
        disabled,
        ...errorProps(path, message),
      },
      error: fieldError(path, message),
    };
  };
  const itemType = bind("itemType");
  const quantity = bind("quantity");
  const logoColours = bind("logoColours");
  const budget = bind("budget");
  const sourcingCityCode = bind("sourcingCityCode");

  return (
    <Card className="p-4">
      {/* Named by aria-label rather than a <legend>, which must be the
          fieldset's first child and so cannot share a row with Remove. */}
      <fieldset aria-label={`Line ${index + 1}`} className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-semibold" aria-hidden>
            Line {index + 1}
          </p>
          {canRemove && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onRemove}
              disabled={disabled}
              aria-label={`Remove line ${index + 1}`}
            >
              <Trash2 className="size-4" aria-hidden />
              Remove
            </Button>
          )}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor={id("itemType")}>Item type</Label>
            <Input
              {...itemType.input}
              placeholder="e.g. Ceramic mug, white, 300ml"
            />
            {itemType.error}
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor={id("quantity")}>Quantity</Label>
            <Input
              {...quantity.input}
              placeholder="e.g. 500, or 100 / 250 / 500"
            />
            <p className="text-xs text-muted-foreground">
              One number, or the price breaks to quote.
            </p>
            {quantity.error}
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor={id("logoColours")}>Logo colours</Label>
            <Textarea
              {...logoColours.input}
              rows={2}
              placeholder={'e.g. Navy and white, Pantone 2945C — or "None"'}
            />
            {logoColours.error}
          </div>
          <div className="space-y-1">
            <Label htmlFor={id("budget")}>Budget (optional)</Label>
            <Input {...budget.input} placeholder="e.g. £2000, or under £1000" />
            {budget.error}
          </div>
          <div className="space-y-1">
            <Label htmlFor={id("sourcingCityCode")}>
              Sourcing City code (optional)
            </Label>
            <Input
              {...sourcingCityCode.input}
              placeholder="The product ID in Promo Office's catalogue"
            />
            {sourcingCityCode.error}
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor={id("files")}>Logo (optional)</Label>
            <Input
              id={id("files")}
              type="file"
              multiple
              onChange={(e) => {
                onAddFiles(e.target.files);
                e.target.value = "";
              }}
              disabled={disabled}
            />
            {line.files.length > 0 && (
              <ul className="space-y-1">
                {line.files.map((file) => (
                  <li
                    key={file.name}
                    className="flex items-center gap-2 text-sm"
                  >
                    <Paperclip
                      className="size-3.5 text-muted-foreground"
                      aria-hidden
                    />
                    <span>{file.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatBytes(file.size)}
                    </span>
                    <button
                      type="button"
                      className="ml-auto text-muted-foreground hover:text-foreground"
                      aria-label={`Remove ${file.name}`}
                      onClick={() => onRemoveFile(file.name)}
                    >
                      <X className="size-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </fieldset>
    </Card>
  );
}

function RequestSummary({
  customer,
  projectName,
  deadline,
  notes,
  lines,
  deliverySelection,
  deliveryNotes,
  compact = false,
}: {
  customer: EntityOption | null;
  projectName: string;
  /** `YYYY-MM-DD`, or empty when not picked yet. */
  deadline: string;
  notes: string;
  lines: RequestLineDraft[];
  deliverySelection: DeliverySelection | null;
  deliveryNotes: string;
  compact?: boolean;
}) {
  const gap = (text: string) => (
    <span className="text-muted-foreground italic">{text}</span>
  );
  const fileCount = lines.reduce((sum, line) => sum + line.files.length, 0);
  return (
    <Card
      role="region"
      aria-label="Merchandise request summary"
      className={compact ? "p-4 space-y-3" : "p-5 space-y-3"}
    >
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Merchandise request
      </h3>
      <dl className="space-y-2 text-sm">
        <div>
          <dt className="text-xs text-muted-foreground">Customer</dt>
          <dd>{customer ? customer.label : gap("Not picked yet")}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Project name</dt>
          <dd className="break-words">
            {projectName.trim() || gap("Not named yet")}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Deadline</dt>
          <dd>
            {deadline ? formatLongDateOrDash(deadline) : gap("Not picked yet")}
          </dd>
        </div>
        {notes.trim() && (
          <div>
            <dt className="text-xs text-muted-foreground">Notes</dt>
            <dd className="break-words whitespace-pre-line">{notes.trim()}</dd>
          </div>
        )}
        <div>
          <dt className="text-xs text-muted-foreground">Request lines</dt>
          <dd>
            <ol className="space-y-1">
              {lines.map((line, index) => (
                <li key={line.key} className="break-words">
                  <span className="text-muted-foreground">{index + 1}.</span>{" "}
                  {line.itemType.trim() || gap("Not described yet")}
                  {line.quantity.trim() && (
                    <span className="text-muted-foreground">
                      {" "}
                      · {line.quantity.trim()}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Delivery</dt>
          <dd>
            {deliverySelection
              ? DELIVERY_MODE_LABELS[deliverySelection.mode]
              : gap("Not chosen")}
            {deliveryNotes.trim() && (
              <span className="block text-xs text-muted-foreground">
                {deliveryNotes.trim()}
              </span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Logos</dt>
          <dd>
            {fileCount > 0
              ? `${fileCount} file${fileCount === 1 ? "" : "s"}`
              : gap("None")}
          </dd>
        </div>
      </dl>
      <p className="text-xs text-muted-foreground border-t border-border pt-2">
        No price yet — the merchandise team quotes this in Promo Office.
      </p>
    </Card>
  );
}

function CreatedScreen({
  request,
  uploads,
  onRetryUpload,
  onBackToEstimates,
}: {
  request: MerchandiseDraftEstimate;
  uploads: FileUpload[];
  onRetryUpload: (index: number) => void;
  onBackToEstimates: () => void;
}) {
  const failedCount = uploads.filter((u) => u.status === "failed").length;
  return (
    <div className="space-y-4 max-w-3xl mx-auto">
      <button
        type="button"
        onClick={onBackToEstimates}
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Back to estimates
      </button>
      <Card className="p-6 space-y-5">
        <div className="flex items-start gap-3">
          <CheckCircle className="size-6 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">
              Merchandise request created
            </h2>
            <p className="text-sm text-muted-foreground">
              Ticket raised to the merchandise team. They will quote in Promo
              Office and the price will appear on the request.
            </p>
          </div>
        </div>
        <div className="rounded-md border bg-muted/40 p-4">
          <div className="text-xs text-muted-foreground mb-1">Reference</div>
          <div className="text-2xl font-mono font-semibold select-all">
            {request.reference}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            The merchandise team types this into the Promo Office quote&apos;s
            Customer Ref, which attaches it to this request, and the project
            name into its Project Name.
          </p>
        </div>
        <dl className="grid gap-4 sm:grid-cols-2 text-sm">
          <div>
            <dt className="text-xs text-muted-foreground">Project name</dt>
            <dd className="break-words">{request.request.projectName}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Customer</dt>
            <dd>
              {request.customer.name}{" "}
              <span className="font-mono text-xs text-muted-foreground">
                {request.customer.code}
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Deadline</dt>
            <dd>{formatLongDateOrDash(request.request.deadline)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Request</dt>
            <dd>{describeRequestLineCount(request.request.lines.length)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Ticket</dt>
            {/* Null when ticket creation failed. The request still stands —
                losing a CSR's enquiry over a ticketing failure would be the
                worse outcome — so the screen says what to do instead. */}
            <dd className="flex items-center gap-1.5">
              <Ticket className="size-3.5 text-muted-foreground" aria-hidden />
              {request.merchandiseTeamTicket ? (
                <>
                  <span className="font-mono">
                    {request.merchandiseTeamTicket.id}
                  </span>
                  <span className="text-muted-foreground">
                    · Merchandise team
                  </span>
                </>
              ) : (
                <span className="text-muted-foreground">
                  No ticket was raised — send the reference to the merchandise
                  team directly.
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">State</dt>
            <dd>Awaiting quote</dd>
          </div>
        </dl>
        {uploads.length > 0 && (
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Logos
            </h3>
            <ul className="space-y-1">
              {uploads.map((upload, index) => (
                <li
                  key={`${upload.requestLineId}-${upload.file.name}`}
                  className="flex flex-wrap items-center gap-2 text-sm"
                >
                  <Paperclip
                    className="size-3.5 text-muted-foreground"
                    aria-hidden
                  />
                  <span className="text-xs text-muted-foreground">
                    Line {upload.lineNo}
                  </span>
                  <span>{upload.file.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {formatBytes(upload.file.size)}
                  </span>
                  {upload.status === "done" && (
                    <span className="text-xs text-green-700 dark:text-green-400">
                      Attached
                    </span>
                  )}
                  {upload.status === "uploading" && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Loader2 className="size-3 animate-spin" aria-hidden />
                      Uploading…
                    </span>
                  )}
                  {upload.status === "pending" && (
                    <span className="text-xs text-muted-foreground">
                      Waiting
                    </span>
                  )}
                  {upload.status === "failed" && (
                    <>
                      <span className="text-xs text-[color:var(--color-destructive)]">
                        Upload failed
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onRetryUpload(index)}
                      >
                        Retry
                      </Button>
                    </>
                  )}
                </li>
              ))}
            </ul>
            {failedCount > 0 && (
              // The draft and its reference are safe; only the files are
              // outstanding, and saying which is the difference between a
              // retry and a re-raise.
              <p className="text-xs text-muted-foreground max-w-prose">
                {failedCount === 1 ? "One file" : `${failedCount} files`} did
                not reach the request. The merchandise request and its reference
                are safe — retry above, or send the files to the merchandise
                team with the reference.
              </p>
            )}
          </section>
        )}
        <div className="flex flex-wrap gap-2">
          <Link
            to={paths.estimateDetail({ id: request.id })}
            className="inline-flex items-center gap-2 rounded-md bg-[color:var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
          >
            View merchandise request
            <ArrowRight className="size-4" />
          </Link>
          <Button variant="outline" onClick={onBackToEstimates}>
            Back to estimates
          </Button>
        </div>
      </Card>
    </div>
  );
}
